import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { WORKER_TYPES } from 'api/workers/constants'
import type { PartialWorkerData } from 'api/workers/types'

import { getWorkerList, getWorker, getWorkerListConcat, selectWorkersStatus } from 'slices/workersSlice'
import { getWorkspaceList, selectWorkspacesStatus } from 'slices/workspacesSlice'

import styles from 'components/Workers/WorkerList.module.scss'
import { WORKER_TYPES_LABEL } from 'components/common/constants'
import type { ListItem, FilterItem } from 'components/common/types'

import type { ChangeEvent, UIEvent } from 'react'

const NO_WORKSPACE_ASSIGNED = 0
const SPOT_MEMBER_GROUP_FILTER_ID = -1
const WORKER_LIST_LIMIT = 50
const SCROLL_THRESHOLD = 2.0

export type WorkerFilterParams = {
  workspaceIds?: number[]
  includeSpotWorker?: boolean
  includeUnaffiliatedWorker?: boolean
  nameOrWmsMemberId?: string
}

export type UseWorkerListPanelOptions = {
  regularMemberOnly?: boolean
  showDefaultShiftError?: boolean
}

export const useWorkerListPanel = (options: UseWorkerListPanelOptions = {}) => {
  const { regularMemberOnly = false, showDefaultShiftError = false } = options
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | undefined>(undefined)
  const [filterItems, setFilterItems] = useState<FilterItem[]>([])
  const [filterWord, setFilterWord] = useState('')
  const [filterParams, setFilterParams] = useState<WorkerFilterParams>({})

  const dispatch = useDispatch()

  const { partialWorkers, worker, isLastPage } = useSelector(selectWorkersStatus, shallowEqual)
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)

  useEffect(() => {
    dispatch(getWorkerList({ limit: WORKER_LIST_LIMIT, includeSpotWorker: !regularMemberOnly }))
    dispatch(getWorkspaceList())
  }, [dispatch, regularMemberOnly])

  const listItems = useMemo(
    () =>
      partialWorkers.reduce((acc: ListItem[], cur: PartialWorkerData) => {
        const data = filterItems.find(item => {
          if (cur.workerType === WORKER_TYPES.SPOT_MEMBER) {
            return item.key === SPOT_MEMBER_GROUP_FILTER_ID
          }
          if (cur.workspaceId === null) {
            return item.key === NO_WORKSPACE_ASSIGNED
          }
          return item.key === cur.workspaceId
        })?.label

        if (data) {
          acc.push({
            id: cur.id,
            title: cur.name,
            data,
            option:
              showDefaultShiftError && cur.isDefaultShiftError
                ? React.createElement('i', { className: 'icf-alert font-large text-warning' })
                : undefined,
          })
        }
        return acc
      }, []),
    [partialWorkers, filterItems, showDefaultShiftError]
  )

  const hasListItems = useMemo(() => listItems.length > 0, [listItems])

  useEffect(() => {
    setSelectedWorkerId(prev => {
      // 0件の場合、undefinedで未登録画面を表示させる
      if (listItems.length === 0) {
        return
      }
      // 初期化時とアイテム削除時
      if (prev === undefined || !listItems.some(item => item.id === prev)) {
        return Number(listItems[0].id)
      }
      return prev
    })
  }, [listItems])

  useEffect(() => {
    if (!selectedWorkerId) {
      return
    }
    dispatch(getWorker(selectedWorkerId))
  }, [selectedWorkerId, dispatch])

  useEffect(() => {
    const initFilterItem: FilterItem[] = [
      ...partialWorkspaces.map<FilterItem>(workspace => ({
        key: workspace.id,
        label: workspace.name,
        checked: true,
      })),
      { key: NO_WORKSPACE_ASSIGNED, label: 'ワークスペース未所属', checked: true },
      ...(!regularMemberOnly
        ? [
            {
              key: SPOT_MEMBER_GROUP_FILTER_ID,
              label: WORKER_TYPES_LABEL[1].value,
              checked: true,
              topBorder: React.createElement('hr', { className: styles.spotMemberBorder }),
            },
          ]
        : []),
    ]

    setFilterItems(initFilterItem)
    setFilterWord('')
  }, [partialWorkspaces, regularMemberOnly])

  const handleSearchInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFilterWord(event.target.value)
  }, [])

  const handleFilterChange = useCallback((items: FilterItem[]) => {
    setFilterItems(items)
  }, [])

  const handleGetWorkerList = useCallback(() => {
    const workspaceIds = filterItems
      .filter(f => f.checked && f.key !== NO_WORKSPACE_ASSIGNED && f.key !== SPOT_MEMBER_GROUP_FILTER_ID)
      .map(f => Number(f.key))
    const includeSpotWorker =
      !regularMemberOnly && filterItems.some(f => f.checked && f.key === SPOT_MEMBER_GROUP_FILTER_ID)
    const includeUnaffiliatedWorker = filterItems.some(f => f.checked && f.key === NO_WORKSPACE_ASSIGNED)
    const nameOrWmsMemberId = filterWord || undefined

    const params = { nameOrWmsMemberId, workspaceIds, includeSpotWorker, includeUnaffiliatedWorker }
    setFilterParams(params)

    dispatch(getWorkerList({ ...params, limit: WORKER_LIST_LIMIT }))
  }, [dispatch, filterItems, filterWord, regularMemberOnly])

  const handleWorkerIdChange = useCallback(
    (workerId: string | number) => {
      if (worker?.id !== workerId) {
        setSelectedWorkerId(Number(workerId))
      }
    },
    [worker?.id]
  )

  const clearFilterWord = useCallback(() => {
    setFilterWord('')
  }, [])

  const disabledSearchButton = useMemo(() => filterItems.every(item => !item.checked), [filterItems])

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (isLastPage !== false || partialWorkers.length === 0) {
        return
      }

      const el = e.currentTarget
      // 一番下までスクロールしたかを判定、誤差を許容するために閾値を設ける
      if (el.scrollTop + el.clientHeight + SCROLL_THRESHOLD < el.scrollHeight) {
        return
      }

      const last = partialWorkers.slice(-1)[0]
      dispatch(
        getWorkerListConcat({
          ...filterParams,
          limit: WORKER_LIST_LIMIT,
          includeSpotWorker: !regularMemberOnly,
          cursor: [last.workerType, last.wmsMemberId || '', last.id],
        })
      )
    },
    [isLastPage, dispatch, filterParams, partialWorkers, regularMemberOnly]
  )

  return {
    // State
    selectedWorkerId,
    filterItems,
    filterWord,
    filterParams,
    worker,
    partialWorkers,
    partialWorkspaces,
    listItems,
    hasListItems,
    disabledSearchButton,
    // Actions
    setSelectedWorkerId,
    handleSearchInput,
    handleFilterChange,
    handleGetWorkerList,
    handleWorkerIdChange,
    clearFilterWord,
    handleScroll,
    // Constants
    WORKER_LIST_LIMIT,
  }
}
